<script>
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsTable',
        inheritAttrs: false,
        data() {
            return {
                provided: {
                    multiselection: this.multiselection,
                    rowNumbersCol: this.rowNumbers,
                    onDark: this.onDark,
                    size: this.size,
                },
                version: `${name}-${version}`,
            };
        },
        props: {
            fixedFirstCol: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, the first column (and row numbers and multiselection, if present) will stick in place. You may customize the column’s width.',
                },
            },
            multiselection: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, shows the select checkboxes in both the header and rows.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            rowHover: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, hover styling is applied to rows on hover.',
                },
            },
            rowNumbers: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, indicates that table rows will display a row number. The row number per row must be set on the `MdsTr`.',
                },
            },
            showSortable: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sortable indicator is visible in header.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the table.',
                },
            },
            spacious: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies extra top and bottom padding to cells.',
                },
            },
            zebraStripes: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies zebra stripe styling to rows.',
                },
            },
        },
        watch: {
            multiselection() {
                this.provided.multiselection = this.multiselection;
            },
            rowNumbers() {
                this.provided.rowNumbersCol = this.rowNumbers;
            },
            onDark() {
                this.provided.onDark = this.onDark;
            },
            size() {
                this.provided.size = this.size;
            },
        },
        provide() {
            const parentVariation = this.provided;
            return { parentVariation };
        },
        computed: {
            classObject() {
                const obj = {};

                obj[this.$style['mds-table--show-sortable']] = this.showSortable;
                obj[this.$style['mds-table--row-hover']] = this.rowHover;
                obj[this.$style['mds-table--zebra']] = this.zebraStripes;
                obj[this.$style['mds-table--spacious']] = this.spacious;
                obj[this.$style[this.fixedFirstColClass]] = this.fixedFirstCol;

                return obj;
            },
            onDarkClass() {
                const obj = {};

                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-table--${this.size}`];
            },
            fixedFirstColClass() {
                const withSelection = this.multiselection ? '-withSelection' : '';
                const withRowNumbers = this.rowNumbers ? '-withRowNumbers' : '';

                return `mds-table--fixed-column${withSelection}${withRowNumbers}`;
            },
        },
        render(createElement) {
            // Table element options (e.g. classes, attributes)
            const tableOptions = {
                class: [this.$style['mds-table'], this.classObject, this.onDarkClass, this.sizeClass],
                attrs: {
                    'data-mds-version': this.version,
                    ...this.$attrs,
                },
            };

            // If fixedFirstCol, then wrap the table in a div element
            if (this.fixedFirstCol) {
                return createElement(
                    'div',
                    {
                        class: [this.$style['mds-table__scroller'], this.onDarkClass, this.sizeClass],
                        attrs: { tabindex: 0 },
                    },
                    [createElement('table', tableOptions, [this.$slots.default])]
                );
                // Otherwise render the table as the root element
            } else {
                return createElement('table', tableOptions, [this.$slots.default]);
            }
        },
    };
</script>

<style lang="scss" module>
    @import './table.scss';
</style>
